import { Select } from "antd";
import { Suspense, useCallback, useEffect, useTransition } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { useConnectionNodes } from "../Pagination";

import type { DefaultOptionType } from "antd/lib/select";
import type { GroupsFilter_query$key } from "__generated__/GroupsFilter_query.graphql";
import type { GroupsFilterQuery } from "__generated__/GroupsFilterQuery.graphql";
import type { GroupsFilterRefetchQuery } from "__generated__/GroupsFilterRefetchQuery.graphql";
import type { GroupsFilterSelectedGroups_query$key } from "__generated__/GroupsFilterSelectedGroups_query.graphql";
import type { GroupsFilterSelectedGroupsRefetchQuery } from "__generated__/GroupsFilterSelectedGroupsRefetchQuery.graphql";

const groupsFragment = graphql`
  fragment GroupsFilter_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "GroupsFilterRefetchQuery") {
    groups(after: $cursor, first: $count, searchTerm: $searchTerm)
      @connection(key: "GroupsFilter_groups") {
      totalCount
      edges {
        node {
          id
          uuid
          name
        }
      }
    }
  }
`;

const selectedGroupsFragment = graphql`
  fragment GroupsFilterSelectedGroups_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 5 }
    groupUuids: { type: "[String]" }
  )
  @refetchable(queryName: "GroupsFilterSelectedGroupsRefetchQuery") {
    selectedGroups: groups(after: $cursor, first: $count, groupUuids: $groupUuids)
      @connection(key: "GroupsFilter_selectedGroups") {
      edges {
        node {
          id
          uuid
          name
        }
      }
    }
  }
`;

const query = graphql`
  query GroupsFilterQuery($groupUuids: [String]) {
    ...GroupsFilter_query
    ...GroupsFilterSelectedGroups_query @arguments(groupUuids: $groupUuids)
  }
`;

interface Props {
  placeholder: string;
  value: string[];
  onChange: (value: string[]) => void;
}

const GroupsFilter = ({ placeholder, value, onChange }: Props) => {
  const data = useLazyLoadQuery<GroupsFilterQuery>(query, { groupUuids: value || [] });

  const [isPending, startTransition] = useTransition();
  const paginationQuery = usePaginationFragment<GroupsFilterRefetchQuery, GroupsFilter_query$key>(
    groupsFragment,
    data
  );

  const selectedGroupsPaginationQuery = usePaginationFragment<
    GroupsFilterSelectedGroupsRefetchQuery,
    GroupsFilterSelectedGroups_query$key
  >(selectedGroupsFragment, data);

  const { refetch } = selectedGroupsPaginationQuery;
  useEffect(() => {
    startTransition(() => {
      refetch({ groupUuids: value || [] });
    });
  }, [refetch, startTransition, value]);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      startTransition(() => {
        paginationQuery.refetch({ searchTerm });
      });
    },
    [paginationQuery, startTransition]
  );

  const groups = useConnectionNodes(paginationQuery.data?.groups);
  const selectedGroups = useConnectionNodes(selectedGroupsPaginationQuery.data?.selectedGroups);

  const options = groups.map(g => ({ label: g.name, value: g.uuid }));
  const selectedValues = selectedGroups.map(g => ({ label: g.name, value: g.uuid }));

  return (
    <Select<DefaultOptionType[]>
      mode="multiple"
      placeholder={placeholder}
      className="w-72"
      value={selectedValues}
      // @ts-expect-error: ts(2322) antd types are wrong, first parameters seems to always be an array of
      // strings (values)
      onChange={onChange}
      onSearch={handleSearch}
      loading={isPending}
      options={options}
      maxTagCount={5}
      filterOption={false}
    />
  );
};

const SuspenseWrappedGroupsFilter = (props: Props) => {
  return (
    <Suspense
      fallback={
        <Select<DefaultOptionType[]>
          mode="multiple"
          placeholder={props.placeholder}
          className="w-72"
          loading={true}
        />
      }
    >
      <GroupsFilter {...props} />
    </Suspense>
  );
};

export default SuspenseWrappedGroupsFilter;
